import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface PaginationProps {
  /** Total number of items */
  total: number;
  /** Number of items per page */
  limit: number;
  /** Current offset (0-based) */
  offset: number;
  /** Callback when page changes, receives new offset */
  onPageChange: (offset: number) => void;
  /** Optional className for the container */
  className?: string;
  /** Show first/last page buttons (default: true for >5 pages) */
  showFirstLast?: boolean;
  /** Maximum number of page buttons to show (default: 5) */
  maxPageButtons?: number;
}

export function Pagination({
  total,
  limit,
  offset,
  onPageChange,
  className,
  showFirstLast,
  maxPageButtons = 5,
}: PaginationProps) {
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);

  // Don't render if there's only one page or no items
  if (totalPages <= 1) {
    return null;
  }

  const showFirstLastButtons = showFirstLast ?? totalPages > 5;

  // Calculate which page numbers to show
  const getPageNumbers = (): number[] => {
    const pages: number[] = [];
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

    // Adjust start if we're near the end
    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const goToPage = (page: number) => {
    const newOffset = (page - 1) * limit;
    onPageChange(newOffset);
  };

  const pageNumbers = getPageNumbers();

  return (
    <div
      className={cn(
        'flex flex-col sm:flex-row items-center justify-between gap-4 px-2',
        className
      )}
    >
      {/* Page info */}
      <p className="text-sm text-gray-600">
        Síða <span className="font-medium">{currentPage}</span> af{' '}
        <span className="font-medium">{totalPages}</span>
        <span className="text-gray-400 ml-2">
          ({total} {total === 1 ? 'færsla' : 'færslur'})
        </span>
      </p>

      {/* Navigation */}
      <div className="flex items-center gap-1">
        {/* First page */}
        {showFirstLastButtons && (
          <Button
            variant="outline"
            size="icon"
            onClick={() => goToPage(1)}
            disabled={currentPage === 1}
            aria-label="Fyrsta síða"
            className="hidden sm:flex h-9 w-9"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
        )}

        {/* Previous page */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Fyrri síða"
          className="h-9"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          <span className="hidden sm:inline">Fyrri</span>
        </Button>

        {/* Page numbers */}
        <div className="hidden sm:flex items-center gap-1">
          {pageNumbers[0] > 1 && (
            <>
              <Button
                variant="outline"
                size="icon"
                onClick={() => goToPage(1)}
                className="h-9 w-9"
              >
                1
              </Button>
              {pageNumbers[0] > 2 && (
                <span className="px-2 text-gray-400">...</span>
              )}
            </>
          )}

          {pageNumbers.map((page) => (
            <Button
              key={page}
              variant={page === currentPage ? 'default' : 'outline'}
              size="icon"
              onClick={() => goToPage(page)}
              aria-label={`Síða ${page}`}
              aria-current={page === currentPage ? 'page' : undefined}
              className="h-9 w-9"
            >
              {page}
            </Button>
          ))}

          {pageNumbers[pageNumbers.length - 1] < totalPages && (
            <>
              {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                <span className="px-2 text-gray-400">...</span>
              )}
              <Button
                variant="outline"
                size="icon"
                onClick={() => goToPage(totalPages)}
                className="h-9 w-9"
              >
                {totalPages}
              </Button>
            </>
          )}
        </div>

        {/* Mobile page indicator */}
        <span className="sm:hidden px-3 text-sm font-medium text-gray-700">
          {currentPage} / {totalPages}
        </span>

        {/* Next page */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Næsta síða"
          className="h-9"
        >
          <span className="hidden sm:inline">Næsta</span>
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>

        {/* Last page */}
        {showFirstLastButtons && (
          <Button
            variant="outline"
            size="icon"
            onClick={() => goToPage(totalPages)}
            disabled={currentPage === totalPages}
            aria-label="Síðasta síða"
            className="hidden sm:flex h-9 w-9"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default Pagination;
